import React, { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import UsersContext from "../Context/UsersContext";
import DataContext from "../Context/DAtaContext";
import supabase from "../Config/Supabase";

const ease = [0.25, 0.1, 0.25, 1];

const fade = {
  hidden: { opacity: 0, y: 16, filter: "blur(4px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.45, delay: i * 0.07, ease },
  }),
  exit: {
    opacity: 0,
    y: -12,
    filter: "blur(4px)",
    transition: { duration: 0.3, ease },
  },
};

const slide = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.42, ease } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.3, ease } },
};

const STEPS = ["Sender", "Receiver", "Confirm"];

function Avatar({ name, size = 40, color = "emerald" }) {
  const initials = name
    ? name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";
  const palettes = {
    emerald: "bg-emerald-900/40 text-emerald-300 ring-emerald-700/50",
    sky: "bg-sky-900/40 text-sky-300 ring-sky-700/50",
    violet: "bg-violet-900/40 text-violet-300 ring-violet-700/50",
    amber: "bg-amber-900/40 text-amber-300 ring-amber-700/50",
  };
  return (
    <div
      className={`flex items-center justify-center rounded-full ring-1 font-semibold tracking-wide ${palettes[color]}`}
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
}

function FarmerCard({
  farmer,
  onClick,
  selected,
  color = "emerald",
  subtitle,
}) {
  return (
    <motion.button
      onClick={() => onClick(farmer)}
      whileTap={{ scale: 0.985 }}
      transition={{ duration: 0.18 }}
      className={`w-full text-left p-3.5 rounded-xl border transition-all duration-200 flex items-center gap-3 group
        ${
          selected
            ? "border-emerald-500/60 bg-emerald-950/40 shadow-lg shadow-emerald-950/30"
            : "dark:border-white/[0.07]  dark:bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/[0.13]"
        }`}
    >
      <Avatar name={farmer.name} size={38} color={color} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight truncate dark:text-white">
          {farmer.name}
        </p>
        <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 font-mono truncate">
          {subtitle || farmer.phone || `#${farmer.id}`}
        </p>
      </div>
      {selected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 shrink-0"
        >
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path
              d="M1 4l2.5 2.5L9 1"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
      )}
    </motion.button>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <svg
        className="absolute -translate-y-1/2 left-3 top-1/2 dark:text-white/25"
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
      >
        <circle
          cx="6.5"
          cy="6.5"
          r="5"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <path
          d="M10.5 10.5l3 3"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-gray-50 text-sm dark:bg-white/[0.05] border dark:border-white/[0.08] dark:text-white placeholder-white/25 focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.07] transition-all duration-200"
      />
    </div>
  );
}

function StepHeader({ step }) {
  return (
    <div className="flex items-center justify-between mb-7">
      {STEPS.map((label, i) => {
        const num = i + 1;
        const done = step > num;
        const active = step === num;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center gap-1.5">
              <motion.div
                animate={{
                  backgroundColor: done
                    ? "#10b981"
                    : active
                      ? "#10b981"
                      : "rgba(255,255,255,0.08)",
                  borderColor:
                    done || active ? "#10b981" : "rgba(255,255,255,0.1)",
                }}
                transition={{ duration: 0.35 }}
                className="flex items-center justify-center w-8 h-8 border rounded-full"
              >
                {done ? (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    width="12"
                    height="10"
                    viewBox="0 0 12 10"
                    fill="none"
                  >
                    <path
                      d="M1 5l3 3L11 1"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                ) : (
                  <span
                    className={`text-xs font-semibold ${active ? "text-white" : "dark:text-white/30 text-gray-500"}`}
                  >
                    {num}
                  </span>
                )}
              </motion.div>
              <span
                className={`text-[11px] font-medium tracking-wide ${active ? "text-emerald-400" : done ? "dark:text-white/60 text-gray-500" : "dark:text-white/25 text-gray-500"}`}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 mx-3 mb-5">
                <div className="h-px bg-gray-300 dark:bg-white/[0.07] relative overflow-hidden rounded-full">
                  <motion.div
                    className="absolute inset-0 bg-emerald-500"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: step > num ? 1 : 0 }}
                    style={{ transformOrigin: "left" }}
                    transition={{ duration: 0.5, ease }}
                  />
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function PartyCard({ label, farmer, balance }) {
  if (!farmer) return null;
  return (
    <div className="flex-1 min-w-0 rounded-xl border border-gray-400 dark:border-white/[0.07] bg-white/[0.03] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 mb-3">
        {label}
      </p>
      <div className="flex items-center gap-2.5">
        <div className="hidden md:block">
          <Avatar
            name={farmer.name}
            size={34}
            color={label === "From" ? "sky" : "violet"}
          />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate dark:text-white">
            {farmer.name}
          </p>
          <p className="font-mono text-xs truncate dark:text-white/35">
            #{farmer.id}
          </p>
        </div>
      </div>
      {balance !== undefined && (
        <div className="mt-3 pt-3 border-t border-gray-400 dark:border-white/[0.06]">
          <p className="text-[10px] text-gray-500 dark:text-white/30 uppercase tracking-widest mb-0.5">
            Balance
          </p>
          <p className="font-mono text-base font-bold text-emerald-400">
            {balance?.toLocaleString()}{" "}
            <span className="text-xs font-normal text-gray-500 dark:text-white/40">
              TZS
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

function MoneyTransfer() {
  const { farmers } = useContext(UsersContext);
  const { Payment, fetchPayments } = useContext(DataContext);

  const [step, setStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTerms, setSearchTerms] = useState("");
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  const [transferAmount, setTransferAmount] = useState("");
  const [senderBalance, setSenderBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const filteredFarmers =
    farmers?.filter(
      (f) =>
        f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.id?.toString().includes(searchTerm),
    ) || [];

  const availableReceivers =
    farmers?.filter((f) => f.id !== selectedSender?.id) || [];

  const avafilteredFarmer =
    availableReceivers?.filter(
      (f) =>
        f.name?.toLowerCase().includes(searchTerms.toLowerCase()) ||
        f.id?.toString().includes(searchTerms),
    ) || [];

  useEffect(() => {
    if (selectedSender) {
      const senderPayment = Payment?.find(
        (p) => p.farmer_id === selectedSender.uuid,
      );
      if (senderPayment) {
        setSenderBalance(senderPayment.amount || 0);
        setStep(2);
        setError("");
      } else {
        setError("This farmer has no payment record or insufficient balance.");
        setSenderBalance(0);
      }
    }
  }, [selectedSender, Payment]);

  const handleSenderSelect = (farmer) => {
    setSelectedSender(farmer);
    setSearchTerm("");
    setError("");
    setSuccess("");
  };

  const handleReceiverSelect = (farmer) => {
    setSelectedReceiver(farmer);
    setStep(3);
  };

  const handleTransfer = async () => {
    const amount = parseFloat(transferAmount);
    if (!amount || amount <= 0) return setError("Please enter a valid amount.");
    if (amount > senderBalance) return setError("Insufficient balance.");

    setLoading(true);
    setError("");

    try {
      const senderPayment = Payment.find(
        (p) => p.farmer_id === selectedSender.uuid,
      );
      const { error: senderErr } = await supabase
        .from("payments")
        .update({
          amount: senderBalance - amount,
          updated_at: new Date().toISOString(),
        })
        .eq("id", senderPayment.id);

      if (senderErr) throw new Error(senderErr.message);

      const { error: receiverErr } = await supabase.from("payments").insert([
        {
          farmer_id: selectedReceiver.uuid,
          amount,
          officer_id: selectedReceiver?.creator_id,
          payment_method: "Tigo Pesa",
          created_at: new Date().toISOString(),
          synced_at: new Date().toISOString(),
        },
      ]);

      if (receiverErr) throw new Error(receiverErr.message);

      const { error: txErr } = await supabase.from("transactions").insert([
        {
          sender_id: selectedSender.uuid,
          receiver_id: selectedReceiver.uuid,
          amount,
          transaction_type: "transfer",
          status: "completed",
          created_at: new Date().toISOString(),
        },
      ]);

      if (txErr) console.warn("Transaction log failed:", txErr.message);

      setSuccess(
        `${amount.toLocaleString()} TZS transferred to ${selectedReceiver.name}`,
      );
      fetchPayments();
      setTimeout(resetForm, 3500);
    } catch (err) {
      setError(err.message || "Transfer failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedSender(null);
    setSelectedReceiver(null);
    setTransferAmount("");
    setSenderBalance(0);
    setSearchTerm("");
    setSearchTerms("");
    setError("");
    setSuccess("");
  };

  const pct = transferAmount
    ? Math.min(100, (parseFloat(transferAmount) / senderBalance) * 100)
    : 0;

  return (
    <div className="flex items-start justify-center min-h-screen py-5 font-sans">
      <motion.div
        className="w-full md:max-w-6xl"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease }}
      >
        {/* Header */}
        <motion.div
          className="flex-col hidden gap-3 mb-6 md:flex sm:mb-8 sm:flex-row sm:items-end sm:justify-between"
          custom={0}
          variants={fade}
          initial="hidden"
          animate="visible"
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 border sm:w-11 sm:h-11 rounded-xl bg-emerald-500/10 border-emerald-500/20 shrink-0">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path
                  d="M2 7h13M11 3l4 4-4 4"
                  stroke="#10b981"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 13H5M9 17l-4-4 4-4"
                  stroke="#10b981"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.55"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold tracking-tight truncate dark:text-white">
                Transfer Investment
              </h1>
              <p className="mt-0.5 text-xs sm:text-sm dark:text-white/50">
                Move funds between farmer accounts
              </p>
            </div>
          </div>

          <div className="items-center self-start hidden gap-2 sm:flex shrink-0 sm:self-auto">
            <span className="text-[11px] font-semibold tracking-widest uppercase text-gray-500 dark:text-white/30">
              Step {step} of {STEPS.length}
            </span>
            <span className="text-[11px] font-semibold tracking-widest uppercase px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              {STEPS[step - 1]}
            </span>
          </div>
        </motion.div>

        {/* Card */}
        <motion.div
          className="rounded-2xl border bg-white dark:border-white/[0.07] dark:bg-gray-900 backdrop-blur-xl overflow-hidden"
          custom={1}
          variants={fade}
          initial="hidden"
          animate="visible"
        >
          <div className="p-6">
            <StepHeader step={step} />

            {/* Alerts */}
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  key="err"
                  variants={fade}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="flex items-start gap-2.5 mb-5 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                >
                  <svg
                    className="shrink-0 mt-0.5"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <circle
                      cx="7"
                      cy="7"
                      r="6"
                      stroke="currentColor"
                      strokeWidth="1.4"
                    />
                    <path
                      d="M7 4v3.5M7 10h.01"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  {error}
                </motion.div>
              )}
              {success && (
                <motion.div
                  key="ok"
                  variants={fade}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="flex items-start gap-2.5 mb-5 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm"
                >
                  <svg
                    className="shrink-0 mt-0.5"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <circle
                      cx="7"
                      cy="7"
                      r="6"
                      stroke="currentColor"
                      strokeWidth="1.4"
                    />
                    <path
                      d="M4.5 7l1.8 1.8L9.5 5"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Steps */}
            <AnimatePresence mode="wait">
              {/* Step 1 */}
              {step === 1 && (
                <motion.div
                  key="s1"
                  variants={slide}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <p className="mb-4 text-xs font-semibold tracking-widest uppercase dark:text-white/50">
                    Choose who to transfer from
                  </p>
                  <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search farmer by name or ID…"
                  />
                  <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-0.5 custom-scroll">
                    <AnimatePresence>
                      {filteredFarmers.length === 0 ? (
                        <p className="py-8 text-sm text-center dark:text-white/40">
                          No farmers found
                        </p>
                      ) : (
                        filteredFarmers?.slice(0, 20)?.map((f, i) => (
                          <motion.div
                            key={f.id}
                            custom={i}
                            variants={fade}
                            initial="hidden"
                            animate="visible"
                          >
                            <FarmerCard
                              farmer={f}
                              onClick={handleSenderSelect}
                              selected={selectedSender?.id === f.id}
                              color="sky"
                              subtitle={f.phone || `#${f.id}`}
                            />
                          </motion.div>
                        ))
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <motion.div
                  key="s2"
                  variants={slide}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {/* Sender summary */}
                  <div className="flex items-center gap-3 mb-5 p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <Avatar name={selectedSender?.name} size={36} color="sky" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold tracking-widest text-gray-500 uppercase dark:text-white/35">
                        Sender
                      </p>
                      <p className="text-sm font-semibold dark:text-white truncate mt-0.5">
                        {selectedSender?.name}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-gray-500 dark:text-white/30 uppercase tracking-widest">
                        Balance
                      </p>
                      <p className="font-mono text-sm font-bold text-emerald-400">
                        {senderBalance?.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <p className="mb-4 text-xs font-semibold tracking-widest text-gray-500 uppercase dark:text-white/35">
                    Choose who receives funds
                  </p>
                  <SearchInput
                    value={searchTerms}
                    onChange={setSearchTerms}
                    placeholder="Search farmer by name or ID…"
                  />
                  <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-0.5">
                    {avafilteredFarmer.length === 0 ? (
                      <p className="py-8 text-sm text-center text-gray-500 dark:text-white/25">
                        No farmers found
                      </p>
                    ) : (
                      avafilteredFarmer?.slice(0, 20)?.map((f, i) => (
                        <motion.div
                          key={f.id}
                          custom={i}
                          variants={fade}
                          initial="hidden"
                          animate="visible"
                        >
                          <FarmerCard
                            farmer={f}
                            onClick={handleReceiverSelect}
                            selected={selectedReceiver?.id === f.id}
                            color="violet"
                            subtitle={f.phone || `#${f.id}`}
                          />
                        </motion.div>
                      ))
                    )}
                  </div>

                  <button
                    onClick={() => setStep(1)}
                    className="mt-4 text-xs text-gray-500 dark:text-white/35 hover:text-white/60 flex items-center gap-1.5 transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M8 10L4 6l4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Back to sender selection
                  </button>
                </motion.div>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <motion.div
                  key="s3"
                  variants={slide}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {/* From / To */}
                  <div className="flex gap-3 mb-5">
                    <PartyCard
                      label="From"
                      farmer={selectedSender}
                      balance={senderBalance}
                    />
                    <div className="flex items-center justify-center pt-4">
                      <div className="w-8 h-8  rounded-full dark:bg-white/[0.05] bg-gray-50 border border-gray-400 dark:border-white/[0.08] flex items-center justify-center">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M2 7h10M8 3l4 4-4 4"
                            stroke="rgba(255,255,255,0.4)"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                    <PartyCard label="To" farmer={selectedReceiver} />
                  </div>

                  {/* Amount input */}
                  <div className="mb-5">
                    <label className="block mb-3 text-xs font-semibold tracking-widest text-gray-500 uppercase dark:text-white/35">
                      Amount to transfer
                    </label>
                    <div className="relative">
                      <span className="absolute text-sm font-semibold text-gray-500 -translate-y-1/2 left-4 top-1/2 dark:text-white/40">
                        TZS
                      </span>
                      <input
                        type="number"
                        value={transferAmount}
                        onChange={(e) => {
                          setTransferAmount(e.target.value);
                          setError("");
                        }}
                        placeholder="0"
                        min="0"
                        max={senderBalance}
                        step="0.01"
                        className="w-full pl-14 pr-4 py-3.5 rounded-xl border-gray-400 text-gray-700 text-lg font-bold dark:text-white bg-white/[0.05] border dark:border-white/[0.08] focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.07] transition-all duration-200 font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>

                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-[11px] text-gray-500 dark:text-white/25 mb-1.5">
                        <span>0</span>
                        <span
                          className={
                            pct > 90
                              ? "text-red-400"
                              : "text-gray-500 dark:text-white/25"
                          }
                        >
                          Max {senderBalance?.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
                        <motion.div
                          className={`h-full rounded-full ${pct > 90 ? "bg-red-500" : "bg-emerald-500"}`}
                          animate={{ width: `${Math.min(pct, 100)}%` }}
                          transition={{ duration: 0.25 }}
                        />
                      </div>
                    </div>

                    {/* Quick amounts */}
                    <div className="flex gap-2 mt-3">
                      {[25, 50, 75, 100].map((p) => (
                        <button
                          key={p}
                          onClick={() =>
                            setTransferAmount(
                              ((senderBalance * p) / 100).toFixed(0),
                            )
                          }
                          className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-white/40 bg-white/[0.04] border border-gray-400 dark:border-white/[0.06] hover:bg-white/[0.08] hover:text-white/70 transition-all"
                        >
                          {p}%
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Transfer summary */}
                  {transferAmount && parseFloat(transferAmount) > 0 && (
                    <motion.div
                      variants={fade}
                      initial="hidden"
                      animate="visible"
                      className="mb-5 p-4 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20"
                    >
                      <p className="mb-2 text-xs font-semibold tracking-widest uppercase text-emerald-400/70">
                        Summary
                      </p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-white/40">
                            Amount
                          </span>
                          <span className="font-mono font-bold text-white">
                            {parseFloat(transferAmount).toLocaleString()} TZS
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-white/40">
                            Remaining balance
                          </span>
                          <span className="font-mono font-semibold text-white/70">
                            {(
                              senderBalance - parseFloat(transferAmount)
                            ).toLocaleString()}{" "}
                            TZS
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(2)}
                      className="px-4 py-3 rounded-xl text-sm text-gray-500 border-gray-400 font-semibold dark:text-white/50 bg-white/[0.04] border dark:border-white/[0.07] hover:bg-white/[0.07] hover:text-white/70 transition-all"
                    >
                      ← Back
                    </button>
                    <motion.button
                      onClick={handleTransfer}
                      disabled={
                        loading ||
                        !transferAmount ||
                        parseFloat(transferAmount) <= 0
                      }
                      whileHover={{ scale: loading ? 1 : 1.015 }}
                      whileTap={{ scale: loading ? 1 : 0.985 }}
                      className="relative flex-1 py-3 overflow-hidden text-sm font-semibold text-white transition-all duration-200 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <motion.span
                            animate={{ rotate: 360 }}
                            transition={{
                              repeat: Infinity,
                              duration: 0.8,
                              ease: "linear",
                            }}
                            className="inline-block w-4 h-4 border-2 rounded-full border-white/30 border-t-white"
                          />
                          Processing…
                        </span>
                      ) : (
                        "Confirm Transfer"
                      )}
                    </motion.button>
                  </div>

                  <button
                    onClick={() => setStep(2)}
                    className="mt-3 text-xs text-white/25 hover:text-white/40 flex items-center gap-1.5 transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M8 10L4 6l4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Change receiver
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-3.5 border-t border-white/[0.05] flex items-center justify-between">
            <p className="text-[11px] text-gray-500 dark:text-white/20 font-mono">
              {selectedSender && selectedReceiver
                ? `${selectedSender.name} → ${selectedReceiver.name}`
                : "No transfer in progress"}
            </p>
            <button
              onClick={resetForm}
              className="text-[11px] dark:hover:bg-gray-800 dark:text-white/40 border border-gray-400 dark:border-gray-700 rounded-xl px-5 py-1 text-gray-600 hover:text-white/50 transition-colors"
            >
              Reset
            </button>
          </div>
        </motion.div>

        {/* Ambient glow */}
        <div
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none top-1/2 left-1/2 w-96 h-96 -z-10"
          style={{
            background:
              "radial-gradient(circle, rgba(16,185,129,0.04) 0%, transparent 70%)",
          }}
        />
      </motion.div>
    </div>
  );
}

export default MoneyTransfer;
